import { Link, Stack } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Sub-paths, not the barrels: those drag the legacy coreOld graph into every grid test.
import { CopyValueButton } from 'core/elements/CopyValueButton';
import { PartDetails } from 'parts/components/PartDetails/PartDetails';
import { IPart } from 'parts/types';
import { Direction, Level } from 'theme/enums';

// What a Part Finder row names its part by: the Partfiniti part and its manufacturer.
interface IPartFinderPartRow {
  manufacturerId: number;
  manufacturerUrlName?: string | null;
  partNumber: string | null;
}

// The part number opens the part's details, as a quote line's does, with its copy button beside
// it. The details panel finds the part by number and manufacturer, so a row without the
// manufacturer's URL name keeps the number as plain text.
export const PartNumberCell = ({ data, value }: ICellRendererParams<IPartFinderPartRow>) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const { t } = useTranslation();

  if (!data) {
    return value ?? null;
  }

  if (!data.partNumber) {
    return null;
  }

  const part = {
    number: data.partNumber,
    manufacturerId: data.manufacturerId,
    manufacturerUrlName: data.manufacturerUrlName,
  } as Partial<IPart> as IPart;

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {data.manufacturerUrlName ? (
        <Link
          component="button"
          level={Level.BODY_SM}
          onClick={() => setIsDetailsOpen(true)}
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {data.partNumber}
        </Link>
      ) : (
        data.partNumber
      )}

      <CopyValueButton value={data.partNumber} label={t('tooltipCopyPartNumber')} />

      {/* Mounted only while open: one panel per rendered row would otherwise sit in the DOM. */}
      {isDetailsOpen && (
        <PartDetails
          part={part}
          isOpen
          onClose={() => setIsDetailsOpen(false)}
          // Without a part number the Sales History tab never fires its query.
          salesHistoryConfig={{ partNumber: data.partNumber }}
        />
      )}
    </Stack>
  );
};
